import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Loader2,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RowActions } from "@/components/ui/RowActions";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { initials } from "@/lib/utils";
import { useContactList, useDeleteContact } from "../hooks/useContacts";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { NewContactDrawer } from "../components/NewContactDrawer";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import type { ContactListItem } from "../types";

const CONTACT_COLUMNS: ColumnDef[] = [
  { key: "fullName", label: "Name" },
  { key: "companyName", label: "Company" },
  { key: "jobTitle", label: "Job Title" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone", defaultVisible: false },
  { key: "status", label: "Status" },
];

function StatusBadge({ status }: { status: string }) {
  const active = status === "Active";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {status}
    </span>
  );
}

function ContactRow({
  contact,
  onDelete,
  isVisible,
}: {
  contact: ContactListItem;
  onDelete: (id: string, name: string) => void;
  isVisible: (key: string) => boolean;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/contacts/${contact.id}`)}
    >
      {isVisible("fullName") && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-xs font-semibold text-[hsl(210,100%,28%)]">
              {initials(contact.fullName)}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-foreground">
                {contact.fullName}
              </div>
              <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                {contact.code}
              </div>
            </div>
          </div>
        </td>
      )}
      {isVisible("companyName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {contact.companyName ? (
            <div className="inline-flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              {contact.companyName}
            </div>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </td>
      )}
      {isVisible("jobTitle") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {contact.jobTitle ?? "—"}
        </td>
      )}
      {isVisible("email") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {contact.email ?? "—"}
        </td>
      )}
      {isVisible("phone") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {contact.phone ?? contact.mobile ?? "—"}
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <StatusBadge status={contact.status} />
        </td>
      )}
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/contacts/${contact.id}`)}
          onEdit={() => navigate(`/contacts/${contact.id}?edit=true`)}
          onDelete={() => onDelete(contact.id, contact.fullName)}
        />
      </td>
    </tr>
  );
}

export default function ContactListPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("contacts", CONTACT_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      companyId: companyFilter || undefined,
    }),
    [search, statusFilter, companyFilter],
  );

  const deleteContact = useDeleteContact();
  const companies = useCompanyList({ limit: 100 });

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useContactList({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    companyId: companyFilter || undefined,
  });

  const contacts = result?.data ?? [];
  const pagination = result?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  };
  const total = pagination.total;

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteContact.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Contacts" },
      ]}
    >
      <div className="w-full space-y-6">
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="contacts"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={CONTACT_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Contact
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load contacts</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search contacts..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Select>
              <Select
                value={companyFilter}
                onChange={(e) => resetPage(setCompanyFilter)(e.target.value)}
                className="w-52"
              >
                <option value="">All Companies</option>
                {companies.data?.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading contacts…
                </div>
              ) : contacts.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No contacts match the current filters.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("fullName") && (
                        <th className="px-6 py-3">Name + Code</th>
                      )}
                      {isVisible("companyName") && (
                        <th className="px-6 py-3">Company</th>
                      )}
                      {isVisible("jobTitle") && (
                        <th className="px-6 py-3">Job Title</th>
                      )}
                      {isVisible("email") && <th className="px-6 py-3">Email</th>}
                      {isVisible("phone") && <th className="px-6 py-3">Phone</th>}
                      {isVisible("status") && (
                        <th className="px-6 py-3">Status</th>
                      )}
                      <th className="px-6 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {contacts.map((c) => (
                      <ContactRow
                        key={c.id}
                        contact={c}
                        onDelete={handleDelete}
                        isVisible={isVisible}
                      />
                    ))}
                  </tbody>
                </table>
              )}

              <div data-pagination="true" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 14px',
                borderTop: '1px solid hsl(210,30%,85%)',
                background: 'hsl(210,30%,97%)',
                fontSize: '12px',
                color: 'hsl(215,16%,47%)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>
                    Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                  </span>
                  <span style={{ marginLeft: '8px' }}>Show</span>
                  <select
                    value={limit}
                    onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                    style={{
                      height: '26px',
                      padding: '0 4px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span>entries</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >‹</button>
                  <span style={{ padding: '0 10px' }}>
                    {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(total / limit)}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                      opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >›</button>
                </div>
              </div>
            </div>
          </>
        )}

        <NewContactDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete contact?"
          description={
            deleteTarget
              ? `Are you sure you want to delete ${deleteTarget.name}? It will be removed from your Contacts list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
